'use client'

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useGuardedRouter } from '@/useGuardedRouter'
import useDebounce from '@/useDebounce'
import { useQuickSearch } from '@/QuickSearch/useQuickSearch'
import { useSearchRecommendations } from '@/useSearchRecommendations'
import { SearchResultSkeleton } from './SearchResultSkeleton'
import { SearchResultItem } from './SearchResultItem'
import { PromotedSearchList } from './PromotedSearchList'
import { CategorySuggestions } from './CategorySuggestions'
import { CreatorSuggestionList } from './CreatorSuggestionList'
import { SearchRecommendations } from './SearchRecommendations'
import { useSearchKeyboard } from './hooks/useSearchKeyboard'
import { getMatchingSearchPromotions } from './searchAutocompletePromotions'
import { getMatchingCreatorSuggestions } from './creatorSuggestions'

interface HeaderSearchProps {
  className?: string
  placeholder?: string
  showResults?: boolean
  variant?: 'mobile' | 'desktop'
}

export const HeaderSearch = ({
  className = '',
  placeholder = 'Search products...',
  showResults = true,
  variant,
}: HeaderSearchProps) => {
  const router = useGuardedRouter()
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  /** Separate refs so focus/scroll-into-view targets the visible input (mobile vs desktop). */
  const mobileSearchInputRef = useRef<HTMLInputElement>(null)
  const desktopSearchInputRef = useRef<HTMLInputElement>(null)
  const resetActiveIndexRef = useRef<(() => void) | null>(null)
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const { trackSearch } = useSearchRecommendations()

  // Memoize pagination and includeTypes to prevent recreating on every render
  const pagination = useMemo(() => ({ page: 1, limit: 8 }), [])
  const includeTypes = useMemo(
    () => ['product'] as ('product' | 'blog' | 'page')[],
    []
  )

  const { results, isLoading, totalCount, facets } = useQuickSearch({
    query: debouncedSearchTerm,
    pagination,
    includeTypes,
    omitSortBy: true,
  })

  const filteredResults = results
    .filter(result => !!result.productUrl)
    .slice(0, 5) // Limit to 5 results, no scrolling
  const promotedMatches = useMemo(
    () => getMatchingSearchPromotions(debouncedSearchTerm),
    [debouncedSearchTerm]
  )
  const creatorSuggestions = useMemo(
    () => getMatchingCreatorSuggestions(results, debouncedSearchTerm),
    [results, debouncedSearchTerm]
  )
  const creatorCount = creatorSuggestions.length
  const promotedCount = promotedMatches.length
  const productCount = filteredResults.length
  const promotionStartIndex = creatorCount
  const productStartIndex = creatorCount + promotedCount
  const viewAllIndex = productStartIndex + productCount
  const hasQuery = debouncedSearchTerm.trim().length > 0
  // Only show loading if we have a query and are actually loading
  // Don't show loading if searchTerm doesn't match debouncedSearchTerm (still typing)
  const isActuallyLoading =
    isLoading && hasQuery && debouncedSearchTerm === searchTerm
  const showDropdown =
    showResults &&
    isSearchFocused &&
    (hasQuery ||
      filteredResults.length > 0 ||
      promotedMatches.length > 0 ||
      !hasQuery)

  const handleSearchSubmit = useCallback(() => {
    const currentSearchTerm = searchTerm.trim()
    if (currentSearchTerm) {
      trackSearch(currentSearchTerm)
      router.push(`/search?query=${encodeURIComponent(currentSearchTerm)}`)
      setSearchTerm('')
      setIsSearchFocused(false)
      resetActiveIndexRef.current?.()
    }
  }, [searchTerm, router, trackSearch])

  // Keyboard navigation
  const { activeIndex, resetActiveIndex, setActiveIndex } = useSearchKeyboard({
    itemCount: creatorCount + promotedCount + productCount + 1, // +1 for the "View all" button
    onSelect: index => {
      if (index < creatorCount && creatorSuggestions[index]) {
        trackSearch(debouncedSearchTerm)
        router.push(creatorSuggestions[index].href)
        setSearchTerm('')
        setIsSearchFocused(false)
        resetActiveIndexRef.current?.()
        return
      }
      const promotedIdx = index - promotionStartIndex
      if (
        promotedIdx >= 0 &&
        promotedIdx < promotedCount &&
        promotedMatches[promotedIdx]
      ) {
        trackSearch(debouncedSearchTerm)
        router.push(promotedMatches[promotedIdx].href)
        setSearchTerm('')
        setIsSearchFocused(false)
        resetActiveIndexRef.current?.()
        return
      }
      const productIdx = index - productStartIndex
      if (
        productIdx >= 0 &&
        productIdx < productCount &&
        filteredResults[productIdx]
      ) {
        trackSearch(debouncedSearchTerm)
        router.push(filteredResults[productIdx].productUrl)
        setSearchTerm('')
        setIsSearchFocused(false)
        resetActiveIndexRef.current?.()
      } else if (index === viewAllIndex) {
        handleSearchSubmit()
      }
    },
    onEscape: () => {
      setIsSearchFocused(false)
      setSearchTerm('')
      resetActiveIndexRef.current?.()
    },
    enabled:
      showDropdown &&
      (promotedMatches.length > 0 || filteredResults.length > 0 || hasQuery),
  })

  // Store resetActiveIndex in ref to avoid dependency issues
  useEffect(() => {
    resetActiveIndexRef.current = resetActiveIndex
  }, [resetActiveIndex])

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      searchRef.current &&
      !searchRef.current.contains(event.target as Node)
    ) {
      setIsSearchFocused(false)
      setSearchTerm('')
      resetActiveIndexRef.current?.()
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        if (activeIndex !== null && activeIndex < creatorCount) {
          if (creatorSuggestions[activeIndex]) {
            trackSearch(debouncedSearchTerm)
            router.push(creatorSuggestions[activeIndex].href)
            setSearchTerm('')
            setIsSearchFocused(false)
            resetActiveIndexRef.current?.()
          }
        } else if (
          activeIndex !== null &&
          activeIndex >= promotionStartIndex &&
          activeIndex < productStartIndex
        ) {
          const promotion = promotedMatches[activeIndex - promotionStartIndex]
          if (promotion) {
            trackSearch(debouncedSearchTerm)
            router.push(promotion.href)
            setSearchTerm('')
            setIsSearchFocused(false)
            resetActiveIndexRef.current?.()
          }
        } else if (
          activeIndex !== null &&
          activeIndex >= productStartIndex &&
          activeIndex < viewAllIndex
        ) {
          const product = filteredResults[activeIndex - productStartIndex]
          if (product) {
            trackSearch(debouncedSearchTerm)
            router.push(product.productUrl)
            setSearchTerm('')
            setIsSearchFocused(false)
            resetActiveIndexRef.current?.()
          }
        } else if (activeIndex === viewAllIndex) {
          handleSearchSubmit()
        } else {
          handleSearchSubmit()
        }
      }
    },
    [
      activeIndex,
      creatorCount,
      creatorSuggestions,
      promotionStartIndex,
      productStartIndex,
      promotedMatches,
      viewAllIndex,
      filteredResults,
      debouncedSearchTerm,
      router,
      trackSearch,
      handleSearchSubmit,
    ]
  )

  const handleResultClick = useCallback(() => {
    trackSearch(debouncedSearchTerm)
    setSearchTerm('')
    setIsSearchFocused(false)
    resetActiveIndexRef.current?.()
  }, [debouncedSearchTerm, trackSearch])

  const handleInputFocus = useCallback(() => {
    setIsSearchFocused(true)
  }, [])

  const handleMobileClose = useCallback(() => {
    setIsSearchFocused(false)
    setSearchTerm('')
    resetActiveIndexRef.current?.()
  }, [])

  // Mobile detection - use CSS classes instead of JS for better SSR
  const isMobile =
    variant === 'mobile' ||
    (typeof window !== 'undefined' && window.innerWidth < 1024)

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Mobile Search Container */}
      <div className="border-b border-gray-200 bg-white px-4 py-1.5 lg:hidden">
        <div className="relative">
          <input
            ref={mobileSearchInputRef}
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onFocus={handleInputFocus}
            onKeyDown={handleInputKeyDown}
            className="min-h-10 w-full rounded-lg border-2 border-gray-200 bg-gray-50 py-2 pl-10 pr-2.5 text-sm transition-all duration-200 focus:border-vi-red focus:bg-white focus:ring-2 focus:ring-vi-red"
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
        </div>
      </div>

      {/* Desktop Search Container */}
      <div className="hidden lg:block relative">
        <input
          ref={desktopSearchInputRef}
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          className={`w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 bg-gray-100 focus:bg-white focus:outline-none focus:ring-0 focus:border-gray-300 transition-all duration-200 hover:bg-gray-50 ${
            showDropdown
              ? 'rounded-t-lg rounded-b-none border-b-0'
              : 'rounded-lg'
          }`}
        />
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
      </div>

      {/* Mobile Full-Screen Overlay */}
      {isMobile && isSearchFocused && (
        <div className="fixed inset-0 bg-black/50 z-50 lg:hidden">
          <div className="bg-white h-full flex flex-col pt-[env(safe-area-inset-top)]">
            {/* Mobile Header */}
            <div className="flex items-center border-b border-gray-200 px-4 py-1.5">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder={placeholder}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  className="min-h-10 w-full rounded-lg border-2 border-gray-200 bg-gray-50 py-2 pl-10 pr-2.5 text-sm focus:border-vi-red focus:bg-white focus:ring-2 focus:ring-vi-red"
                  autoFocus
                />
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              </div>
              <button
                onClick={handleMobileClose}
                className="ml-2 inline-flex min-h-10 min-w-10 shrink-0 items-center justify-center rounded-lg p-1.5 text-gray-500 hover:text-gray-700"
                aria-label="Close search"
                type="button"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Mobile Results */}
            <div className="flex-1 overflow-y-auto pb-[env(safe-area-inset-bottom)]">
              {/* Note: Mobile can scroll since it's full screen, but we still limit results */}
              {isActuallyLoading && hasQuery && (
                <SearchResultSkeleton count={5} />
              )}

              {!isActuallyLoading && hasQuery && filteredResults.length > 0 && (
                <>
                  <CreatorSuggestionList
                    suggestions={creatorSuggestions}
                    activeIndex={activeIndex}
                    onClick={handleResultClick}
                    onMouseEnter={setActiveIndex}
                    onMouseLeave={() => setActiveIndex(null)}
                  />
                  <CategorySuggestions
                    facets={facets}
                    results={filteredResults}
                  />
                  {promotedMatches.length > 0 && (
                    <PromotedSearchList
                      promotions={promotedMatches}
                      activeIndex={
                        activeIndex === null
                          ? null
                          : activeIndex - promotionStartIndex
                      }
                      onClick={handleResultClick}
                      onMouseEnter={index =>
                        setActiveIndex(promotionStartIndex + index)
                      }
                      onMouseLeave={() => setActiveIndex(null)}
                    />
                  )}
                  <div className="py-2">
                    {filteredResults.map((result, index) => (
                      <SearchResultItem
                        key={result.objectId || index}
                        result={result}
                        isActive={activeIndex === productStartIndex + index}
                        onClick={handleResultClick}
                        onMouseEnter={() =>
                          setActiveIndex(productStartIndex + index)
                        }
                        onMouseLeave={() => setActiveIndex(null)}
                      />
                    ))}
                  </div>
                </>
              )}

              {!isActuallyLoading &&
                hasQuery &&
                filteredResults.length === 0 &&
                promotedMatches.length > 0 && (
                  <PromotedSearchList
                    promotions={promotedMatches}
                    activeIndex={
                      activeIndex === null
                        ? null
                        : activeIndex - promotionStartIndex
                    }
                    onClick={handleResultClick}
                    onMouseEnter={index =>
                      setActiveIndex(promotionStartIndex + index)
                    }
                    onMouseLeave={() => setActiveIndex(null)}
                  />
                )}

              {!isActuallyLoading &&
                hasQuery &&
                (promotedMatches.length > 0 || filteredResults.length > 0) && (
                  <div className="border-t border-gray-200 bg-gray-50 rounded-b-lg">
                    <button
                      type="button"
                      onClick={handleSearchSubmit}
                      onMouseEnter={() => setActiveIndex(viewAllIndex)}
                      onMouseLeave={() => setActiveIndex(null)}
                      className={`w-full text-center text-sm text-vi-red font-semibold hover:text-vi-red/80 py-2.5 px-3 transition-colors duration-150 rounded-b-lg cursor-pointer ${
                        activeIndex === viewAllIndex
                          ? 'bg-gray-100'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      {totalCount > 0
                        ? `View all ${totalCount} results for "${searchTerm}"`
                        : `Search the site for "${searchTerm}"`}
                    </button>
                  </div>
                )}

              {!isActuallyLoading &&
                hasQuery &&
                filteredResults.length === 0 &&
                promotedMatches.length === 0 &&
                debouncedSearchTerm === searchTerm && (
                  <div className="p-6 text-center text-gray-600">
                    <p className="text-base mb-3">
                      No results found for "{searchTerm}"
                    </p>
                    <button
                      type="button"
                      onClick={handleSearchSubmit}
                      className="text-base text-vi-red font-semibold hover:text-vi-red/80 transition-colors duration-150"
                    >
                      Search anyway
                    </button>
                  </div>
                )}

              {!hasQuery && <SearchRecommendations onSelect={setSearchTerm} />}
            </div>
          </div>
        </div>
      )}

      {/* Desktop Dropdown */}
      {!isMobile && showDropdown && (
        <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 shadow-xl z-50 flex flex-col rounded-b-lg">
          {isActuallyLoading && hasQuery && <SearchResultSkeleton count={5} />}

          {!isActuallyLoading && hasQuery && filteredResults.length > 0 && (
            <>
              <CreatorSuggestionList
                suggestions={creatorSuggestions}
                activeIndex={activeIndex}
                onClick={handleResultClick}
                onMouseEnter={setActiveIndex}
                onMouseLeave={() => setActiveIndex(null)}
              />
              <CategorySuggestions facets={facets} results={filteredResults} />
              {promotedMatches.length > 0 && (
                <PromotedSearchList
                  promotions={promotedMatches}
                  activeIndex={
                    activeIndex === null
                      ? null
                      : activeIndex - promotionStartIndex
                  }
                  onClick={handleResultClick}
                  onMouseEnter={index =>
                    setActiveIndex(promotionStartIndex + index)
                  }
                  onMouseLeave={() => setActiveIndex(null)}
                />
              )}
              <div className="py-2">
                {filteredResults.map((result, index) => (
                  <SearchResultItem
                    key={result.objectId || index}
                    result={result}
                    isActive={activeIndex === productStartIndex + index}
                    onClick={handleResultClick}
                    onMouseEnter={() =>
                      setActiveIndex(productStartIndex + index)
                    }
                    onMouseLeave={() => setActiveIndex(null)}
                  />
                ))}
              </div>
            </>
          )}

          {!isActuallyLoading &&
            hasQuery &&
            filteredResults.length === 0 &&
            promotedMatches.length > 0 && (
              <PromotedSearchList
                promotions={promotedMatches}
                activeIndex={
                  activeIndex === null
                    ? null
                    : activeIndex - promotionStartIndex
                }
                onClick={handleResultClick}
                onMouseEnter={index =>
                  setActiveIndex(promotionStartIndex + index)
                }
                onMouseLeave={() => setActiveIndex(null)}
              />
            )}

          {!isActuallyLoading &&
            hasQuery &&
            (promotedMatches.length > 0 || filteredResults.length > 0) && (
              <div className="border-t border-gray-200 bg-gray-50 flex-shrink-0 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  onMouseEnter={() => setActiveIndex(viewAllIndex)}
                  onMouseLeave={() => setActiveIndex(null)}
                  className={`w-full text-center text-sm text-vi-red font-semibold hover:text-vi-red/80 py-2.5 px-3 transition-colors duration-150 rounded-b-lg cursor-pointer ${
                    activeIndex === viewAllIndex
                      ? 'bg-gray-100'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {totalCount > 0
                    ? `View all ${totalCount} results for "${searchTerm}"`
                    : `Search the site for "${searchTerm}"`}
                </button>
              </div>
            )}

          {!isActuallyLoading &&
            hasQuery &&
            filteredResults.length === 0 &&
            promotedMatches.length === 0 &&
            debouncedSearchTerm === searchTerm && (
              <div className="p-6 text-center text-gray-600">
                <p className="text-base mb-3">
                  No results found for "{searchTerm}"
                </p>
                <button
                  type="button"
                  onClick={handleSearchSubmit}
                  className="text-base text-vi-red font-semibold hover:text-vi-red/80 transition-colors duration-150"
                >
                  Search anyway
                </button>
              </div>
            )}

          {!hasQuery && <SearchRecommendations onSelect={setSearchTerm} />}
        </div>
      )}
    </div>
  )
}
